import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../../components/navbar/navbar.component";
import SideBar from "../../components/sidebar/sidebar.component";
import { getActiveTowns, updateShipment } from "../../state/actions";

const packagingOptions = ["Box", "Dimensional Weight", "Crating", "Envelope", "Others"];
const deliveryTypes = [
  "Express delivery",
  "Next Am morning delivery",
  "Special delivery",
  "Hold for collection",
];
const rslDeliveryTypes = [
  "Less Than Truck Load",
  "Truck Load",
  "Hold for collection",
];
const displayValue = (value) => value ?? "";

const CustomerShipmentEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [shipment, setShipment] = useState(location.state?.shipment);
  const [towns, setTowns] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!shipment?.cityId) {
      return;
    }

    dispatch(getActiveTowns(shipment.cityId)).then((response) => {
      const activeTowns = response || [];
      setTowns(
        [...activeTowns].sort((firstTown, secondTown) =>
          firstTown.name.localeCompare(secondTown.name)
        )
      );
    });
  }, [dispatch, shipment?.cityId]);

  const handleShipmentChange = (event) => {
    const { name, value } = event.target;
    setShipment({ ...shipment, [name]: value });
  };

  const handleRecipientChange = (event) => {
    const { name, value } = event.target;
    setShipment({
      ...shipment,
      recipient: { ...shipment.recipient, [name]: value },
    });
  };

  const handlePaymentChange = (event) => {
    const { name, value } = event.target;
    setShipment({
      ...shipment,
      shipmentPayment: { ...shipment.shipmentPayment, [name]: Number(value) },
    });
  };

  const handleCratingChange = (event) => {
    const { value } = event.target;
    const payment = shipment.shipmentPayment || {};
    const cratingValue = Number.parseFloat(value) || 0;
    const subTotal =
      (Number.parseFloat(payment.servicePrice) || 0) +
      (Number.parseFloat(payment.onforwardingCharge) || 0) +
      (Number.parseFloat(payment.insuranceValue) || 0) +
      cratingValue +
      (Number.parseFloat(payment.additionalCharge) || 0) +
      (Number.parseFloat(payment.fuelSurcharge) || 0) +
      (Number.parseFloat(payment.demandSurcharge) || 0) +
      (Number.parseFloat(payment.otherSurcharge) || 0) -
      (Number.parseFloat(payment.discount) || 0);
    const vat = Number((subTotal * 0.075).toFixed(2));

    setShipment({
      ...shipment,
      shipmentPayment: {
        ...payment,
        cratingValue: value,
        vat,
        totalAmount: subTotal + vat,
      },
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setIsSaving(true);

    try {
      await dispatch(updateShipment(shipment));
    } catch (error) {
      console.log("Unable to update shipment", error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderInput = (label, name, value, onChange, options = {}) => (
    <div className="col-12 col-sm-4 mb-2">
      <div className="form-group">
        <label htmlFor={name} className="form-label">
          {label}
        </label>
        <input
          id={name}
          name={name}
          type={options.type || "text"}
          disabled={options.disabled}
          className="form-control"
          value={displayValue(value)}
          onChange={onChange}
        />
      </div>
    </div>
  );

  const renderSelect = (label, name, value, onChange, options) => (
    <div className="col-6 col-sm-3 mb-2">
      <div className="form-group">
        <label htmlFor={name} className="form-label">
          {label}
        </label>
        <select
          id={name}
          name={name}
          className="form-control"
          value={displayValue(value)}
          onChange={onChange}
        >
          <option value="">Please Select</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <>
      <NavBar />
      <SideBar />

      <div className="content-page">
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between">
                  <div className="header-title">
                    <h4 className="card-title">Edit Customer Shipment</h4>
                  </div>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>
                </div>
                <div className="card-body">
                  {shipment ? (
                    <form onSubmit={handleSave}>
                      <div className="mb-3">
                        <h5>{shipment.waybillNumber}</h5>
                        <p className="small font-weight-bold mb-2">
                          {shipment.createdOn
                            ? new Date(shipment.createdOn).toLocaleDateString()
                            : null}
                        </p>
                        <div className="row">
                          <div className="d-flex col-12 col-sm-6 col-md-5 col-lg-4 mb-2">
                            <span>Origin Hub:</span>
                            <span className="ml-auto font-weight-bold">
                              {shipment.origin?.name || "N/A"}
                            </span>
                          </div>
                          <div className="d-flex col-12 col-sm-6 col-md-5 col-lg-4 mb-2">
                            <span>Destination Hub:</span>
                            <select
                              name="townId"
                              className="form-control form-control-sm ml-2"
                              value={displayValue(shipment.townId)}
                              onChange={handleShipmentChange}
                            >
                              <option value="">Please Select</option>
                              {towns.map((town) => (
                                <option key={town.id} value={town.id}>
                                  {town.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="card detail-card mb-3">
                        <div className="card-header">
                          <h5 className="card-title mb-0">Sender Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            {renderInput(
                              "Name",
                              "senderName",
                              shipment.senderName,
                              handleShipmentChange
                            )}
                            {renderInput(
                              "Account Number",
                              "senderAccountNo",
                              shipment.senderAccountNo,
                              handleShipmentChange
                            )}
                            {renderInput(
                              "Telephone",
                              "senderPhone",
                              shipment.senderPhone,
                              handleShipmentChange
                            )}
                            <div className="col-12 mb-2">
                              <div className="form-group">
                                <label
                                  htmlFor="senderAddress"
                                  className="form-label"
                                >
                                  Address
                                </label>
                                <input
                                  id="senderAddress"
                                  name="senderAddress"
                                  className="form-control"
                                  value={displayValue(shipment.senderAddress)}
                                  onChange={handleShipmentChange}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card detail-card mb-3">
                        <div className="card-header">
                          <h5 className="card-title mb-0">Recipient Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            {renderInput(
                              "Name",
                              "recipientName",
                              shipment.recipient?.recipientName,
                              handleRecipientChange
                            )}
                            {renderInput(
                              "Email",
                              "email",
                              shipment.recipient?.email,
                              handleRecipientChange
                            )}
                            {renderInput(
                              "Telephone",
                              "phoneNumber",
                              shipment.recipient?.phoneNumber,
                              handleRecipientChange
                            )}
                            {renderInput(
                              "Street Address",
                              "streetAddress",
                              shipment.recipient?.streetAddress,
                              handleRecipientChange
                            )}
                            {renderInput(
                              "City",
                              "city",
                              shipment.recipient?.city,
                              handleRecipientChange
                            )}
                            {renderInput(
                              "State",
                              "state",
                              shipment.recipient?.state,
                              handleRecipientChange
                            )}
                            {renderInput(
                              "Country",
                              "country",
                              shipment.recipient?.country,
                              handleRecipientChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "Postal/ZIP Code",
                              "postalCode",
                              shipment.recipient?.postalCode,
                              handleRecipientChange,
                              { disabled: true }
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="card detail-card mb-3">
                        <div className="card-header">
                          <h5 className="card-title mb-0">Packaging</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            {renderSelect(
                              "Packaging",
                              "packaging",
                              shipment.packaging,
                              handleShipmentChange,
                              packagingOptions
                            )}
                            {renderInput(
                              "Shipment Type",
                              "type",
                              shipment.type,
                              handleShipmentChange,
                              { disabled: true }
                            )}
                            {renderSelect(
                              "Delivery Type",
                              "deliveryType",
                              shipment.deliveryType,
                              handleShipmentChange,
                              shipment.sbuId === 2
                                ? [
                                    ...new Set([
                                      ...rslDeliveryTypes,
                                      shipment.deliveryType,
                                    ]),
                                  ]
                                : [
                                    ...new Set([
                                      ...deliveryTypes,
                                      shipment.deliveryType,
                                    ]),
                                  ]
                            )}
                            {renderInput(
                              "Flyer No",
                              "flyerNo",
                              shipment.flyerNo,
                              handleShipmentChange,
                              { disabled: true }
                            )}
                            {shipment.packaging === "Dimensional Box" && (
                              <>
                                {renderInput(
                                  "Length",
                                  "length",
                                  shipment.length,
                                  handleShipmentChange,
                                  { type: "number", disabled: true }
                                )}
                                {renderInput(
                                  "Width",
                                  "width",
                                  shipment.width,
                                  handleShipmentChange,
                                  { type: "number", disabled: true }
                                )}
                                {renderInput(
                                  "Height",
                                  "height",
                                  shipment.height,
                                  handleShipmentChange,
                                  { type: "number", disabled: true }
                                )}
                              </>
                            )}
                            {renderInput(
                              "Item Quantity",
                              "unit",
                              shipment.unit,
                              handleShipmentChange,
                              { type: "number" }
                            )}
                            {renderInput(
                              "Crating",
                              "boxandCrating",
                              shipment.boxandCrating,
                              handleShipmentChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "Weight",
                              "weight",
                              shipment.weight,
                              handleShipmentChange,
                              { type: "number" }
                            )}
                            <div className="col-12 mb-2">
                              <div className="form-group">
                                <label
                                  htmlFor="contentDescription"
                                  className="form-label"
                                >
                                  Content Description
                                </label>
                                <input
                                  id="contentDescription"
                                  name="contentDescription"
                                  className="form-control"
                                  value={displayValue(shipment.contentDescription)}
                                  onChange={handleShipmentChange}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card detail-card mb-3">
                        <div className="card-header">
                          <h5 className="card-title mb-0">Payment Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            {renderInput(
                              "Service Price",
                              "servicePrice",
                              shipment.shipmentPayment?.servicePrice,
                              handlePaymentChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "Onforwarding",
                              "onforwardingCharge",
                              shipment.shipmentPayment?.onforwardingCharge,
                              handlePaymentChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "Insurance",
                              "insuranceValue",
                              shipment.shipmentPayment?.insuranceValue,
                              handlePaymentChange,
                              { type: "number", disabled: true }
                            )}
                            <div className="col-12 col-sm-4 mb-2">
                              <div className="form-group">
                                <label
                                  htmlFor="cratingValue"
                                  className="form-label"
                                >
                                  Crating Charge
                                </label>
                                <input
                                  id="cratingValue"
                                  name="cratingValue"
                                  type="number"
                                  className="form-control"
                                  value={displayValue(
                                    shipment.shipmentPayment?.cratingValue
                                  )}
                                  onChange={handleCratingChange}
                                />
                              </div>
                            </div>
                            {renderInput(
                              "Other Charge",
                              "additionalCharge",
                              shipment.shipmentPayment?.additionalCharge,
                              handlePaymentChange,
                              { type: "number" }
                            )}
                            {renderInput(
                              "Discount",
                              "discount",
                              shipment.shipmentPayment?.discount,
                              handlePaymentChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "VAT",
                              "vat",
                              shipment.shipmentPayment?.vat,
                              handlePaymentChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "Declared Value",
                              "declaredValue",
                              shipment.shipmentPayment?.declaredValue,
                              handlePaymentChange,
                              { type: "number" }
                            )}
                            {renderInput(
                              "Cash on Delivery",
                              "cashonDelivery",
                              shipment.shipmentPayment?.cashonDelivery,
                              handlePaymentChange,
                              { type: "number" }
                            )}
                            {renderInput(
                              "Total Amount",
                              "totalAmount",
                              shipment.shipmentPayment?.totalAmount,
                              handlePaymentChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "Amount Paid",
                              "paidAmount",
                              shipment.shipmentPayment?.paidAmount,
                              handlePaymentChange,
                              { disabled: true }
                            )}
                            {renderInput(
                              "Payment Method",
                              "paymentMethod",
                              shipment.shipmentPayment?.paymentMethod,
                              handlePaymentChange,
                              { disabled: true }
                            )}
                            {shipment.shipmentPayment?.paymentMethod ===
                              "Bank Transfer" &&
                              renderInput(
                                "Bank",
                                "bankAccount",
                                shipment.shipmentPayment?.bankAccount,
                                handlePaymentChange,
                                { disabled: true }
                              )}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex flex-row-reverse">
                        <div className="col-6 col-sm-3 px-0">
                          <button
                            type="submit"
                            className="btn btn-sm btn-primary w-100"
                            disabled={isSaving}
                          >
                            {isSaving ? "Please wait..." : "Save"}
                          </button>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="text-center mx-auto mt-4">
                      <p className="text-Center">
                        Return to Customer Shipments and select Edit for a shipment.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerShipmentEdit;
